interface Bx24Auth {
  access_token?: string;
  member_id?: string;
  domain?: string;
}

interface Bx24Sdk {
  init(callback: () => void): void;
  getAuth(): Bx24Auth | false;
  fitWindow?(): void;
}

declare global {
  interface Window {
    BX24?: Bx24Sdk;
  }
}

export async function createServerSession(): Promise<{
  token: string;
  user: { id: number; name: string; isAdmin: boolean };
  portal: string;
}> {
  const sdk = await initializeBx24();
  const auth = sdk.getAuth();
  if (
    !auth ||
    !auth.access_token ||
    !auth.member_id ||
    !auth.domain
  ) {
    throw new Error(
      "Не удалось получить авторизацию Битрикс24. Откройте приложение из меню портала."
    );
  }
  const response = await fetch("/api/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(auth)
  });
  const payload = (await response.json()) as Record<string, unknown>;
  if (!response.ok) {
    const errors: Record<string, string> = {
      admin_access_required:
        "Отчёт доступен только администраторам портала.",
      application_not_installed:
        "Установка приложения не завершена. Переустановите приложение.",
      reauthorization_required:
        "Нужно заново авторизовать приложение администратором.",
      tenant_domain_mismatch:
        "Домен портала не совпадает с сохранённой установкой."
    };
    throw new Error(
      errors[String(payload.error)] ??
        "Сервер не подтвердил авторизацию Битрикс24."
    );
  }
  sdk.fitWindow?.();
  return payload as unknown as {
    token: string;
    user: { id: number; name: string; isAdmin: boolean };
    portal: string;
  };
}

async function initializeBx24(): Promise<Bx24Sdk> {
  await ensureBx24Script();
  return new Promise((resolve, reject) => {
    const sdk = window.BX24;
    if (!sdk?.init) {
      reject(
        new Error(
          "BX24 SDK недоступен. Откройте приложение внутри Битрикс24."
        )
      );
      return;
    }
    const timeout = window.setTimeout(
      () => reject(new Error("Битрикс24 не ответил за отведённое время.")),
      10_000
    );
    try {
      sdk.init(() => {
        window.clearTimeout(timeout);
        resolve(sdk);
      });
    } catch (error) {
      window.clearTimeout(timeout);
      reject(error);
    }
  });
}

function ensureBx24Script(): Promise<void> {
  if (window.BX24?.init) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(
    'script[data-bitrix24-sdk="true"]'
  );
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Не удалось загрузить BX24 SDK.")),
        { once: true }
      );
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://api.bitrix24.com/api/v1/";
    script.async = true;
    script.dataset.bitrix24Sdk = "true";
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Не удалось загрузить BX24 SDK.")),
      { once: true }
    );
    document.head.append(script);
  });
}
